import { useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { PreflightCopy } from '@signalvote/shared';
import type { TransactionResendKind } from '@signalvote/shared';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useRequestTransactionResend } from '@/hooks/queries/useFinanceReports';

interface ResendTransactionActionProps {
  reportId: string;
  kind: TransactionResendKind;
  transactionId: string;
  /**
   * #647 — the server-computed "a re-send is already pending for this row" flag (from the ONE shared
   * pending-re-send reader the export-delta generator reads). When true the action starts already in
   * its queued state — the row shows "re-send queued" and the button is gone, so re-sends can't be
   * piled up even across a page reload.
   */
  resendQueued: boolean;
}

/**
 * #647 — the deliberate single-transaction re-send action, living in the report screen's per-row
 * sent-status column (#644) on an ALREADY-SENT EDI row. The AL FCPA portal is append-only (ADR-020),
 * so the automatic export never re-emits a sent row; this is the one sanctioned exception, for when
 * the state lost a transaction or the treasurer deleted it in the portal.
 *
 * Clicking opens a confirm dialog carrying the HONEST warning ("...do not re-send it. Delete it in
 * the portal first...") — a re-send is append-only, so re-sending a transaction the portal still
 * holds would duplicate it. On confirm it records the re-send via #645; the row then reads
 * "re-send queued" and the action disappears (the hook invalidates the detail, and this component
 * also flips locally for instant feedback), so a treasurer can't pile up re-sends on one row.
 * Cancelling records nothing.
 *
 * Rendered only for already-sent EDI rows — a not-yet-sent row rides the next export anyway, and a
 * row the export omits (In-Kind "Other") reads the portal-hand-entry marker instead of this action.
 */
export function ResendTransactionAction({
  reportId,
  kind,
  transactionId,
  resendQueued,
}: ResendTransactionActionProps) {
  const { requestResend } = useRequestTransactionResend();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [queued, setQueued] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Queued from the server (survives reload) OR just queued in this session — either way the action
  // is spent and the row reads "re-send queued".
  const isQueued = resendQueued || queued;

  if (isQueued) {
    return (
      <span
        data-testid={`resend-queued-${kind}-${transactionId}`}
        className="text-xs font-medium text-violet-700"
      >
        {PreflightCopy.ScheduleRowResendQueuedLabel}
      </span>
    );
  }

  const handleResend = async () => {
    setBusy(true);
    setError(null);
    try {
      await requestResend({ reportId, kind, transactionId });
      // Instant feedback; the invalidated detail will also come back with resendQueued = true.
      setQueued(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to queue the re-send');
    } finally {
      setBusy(false);
    }
  };

  return (
    <span className="inline-flex flex-col items-end gap-0.5">
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => {
          setError(null);
          setConfirmOpen(true);
        }}
        disabled={busy}
        data-testid={`resend-action-${kind}-${transactionId}`}
        className="h-auto px-1.5 py-0.5 text-xs text-primary"
      >
        <RefreshCw className="mr-1 h-3 w-3" aria-hidden="true" />
        {busy ? 'Queuing…' : PreflightCopy.ScheduleRowResendActionLabel}
      </Button>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{PreflightCopy.ResendConfirmTitle}</AlertDialogTitle>
            <AlertDialogDescription data-testid={`resend-warning-${kind}-${transactionId}`}>
              {PreflightCopy.ResendConfirmWarning}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={`resend-cancel-${kind}-${transactionId}`}>
              {PreflightCopy.ResendConfirmCancel}
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid={`resend-confirm-${kind}-${transactionId}`}
              onClick={() => {
                setConfirmOpen(false);
                void handleResend();
              }}
            >
              {PreflightCopy.ResendConfirmAccept}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {error && (
        <span
          data-testid={`resend-error-${kind}-${transactionId}`}
          className="text-xs text-destructive"
        >
          {error}
        </span>
      )}
    </span>
  );
}
